import React, {useEffect, useRef, useState, useCallback} from "react";
import {useLocation, useHistory} from "react-router-dom";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {useContextSelector} from "use-context-selector";
import {LocalContext} from "../context/LocalContext";
import {Alert, Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faWifi} from "@fortawesome/free-solid-svg-icons/faWifi";
import {useTranslation} from "react-i18next";
import {Helmet} from "react-helmet";
import Layout from "../common/Layout";
import useGetProvider from "../providers/useGetProvider";
import useCreateClientStatusMessage from "../util/useCreateClientStatusMessage";
import useSubmitUnsucbscribeFromProviderReports from "../providers/useSubmitUnsubscribeFromProviderReports";

const UnsubscribeFromProviderReports = () => {
    const {t} = useTranslation();
    const {executeRecaptcha} = useGoogleReCaptcha();
    const isMounted = useRef(null);
    const history = useHistory();
    const location = useLocation();
    const clientOnline = useContextSelector(LocalContext, (v) => v[0].clientOnline);
    const getAPIDataProvider = useGetProvider();
    const createClientStatusMessage = useCreateClientStatusMessage();
    const submitUnsubscription = useSubmitUnsucbscribeFromProviderReports();

    const [APIDataProvider, setAPIDataProvider] = useState(null);
    const [isLoadingAPIDataProvider, setIsLoadingAPIDataProvider] = useState(false);
    const [isLoadedAPIDataProvider, setIsLoadedAPIDataProvider] = useState(false);
    const [isErrorGetAPIDataProvider, setIsErrorGetAPIDataProvider] = useState(false);
    const [isNotFoundAPIDataProvider, setIsNotFoundAPIDataProvider] = useState(false);
    const [isNotValidUnsubscriptionID, setIsNotValidUnsubscriptionID] = useState(false);

    const [isLoadingSubmitUnsubscription, setIsLoadingSubmitUnsubscription] = useState(false);
    const [isLoadedSubmitUnsubscription, setIsLoadedSubmitUnsubscription] = useState(false);
    const [isErrorSubmitUnsubscription, setIsErrorSubmitUnsubscription] = useState(false);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const extractPathParts = useCallback(() => {
        const unsubscriptionIDFull = location.pathname.split("/")[location.pathname.split("/").length - 1];
        const [unsubscriptionID, providerID] = unsubscriptionIDFull.split("--");
        return {unsubscriptionIDFull, unsubscriptionID, providerID};
    }, [location]);

    const handleGetAPIDataProvider = useCallback(async () => {
        setIsLoadingAPIDataProvider(true);

        const {unsubscriptionID, providerID} = extractPathParts();

        if (!(unsubscriptionID && providerID)) {
            if (isMounted.current) {
                setIsNotValidUnsubscriptionID(true);
                setIsLoadingAPIDataProvider(false);
            }
            return;
        }

        let response;
        try {
            response = await getAPIDataProvider(providerID);
        } catch (error) {
            if (error.status === 404) {
                if (isMounted.current) {
                    setIsNotFoundAPIDataProvider(true);
                    setIsLoadingAPIDataProvider(false);
                }
            } else {
                if (isMounted.current) {
                    setIsErrorGetAPIDataProvider(true);
                    createClientStatusMessage(error.clientStatusMessage || error.createClientStatusMessage, true);
                    setIsLoadingAPIDataProvider(false);
                }
            }
            return;
        }

        if (isMounted.current) {
            setAPIDataProvider(response);
            setIsLoadedAPIDataProvider(true);
            setIsLoadingAPIDataProvider(false);
        }
    }, [extractPathParts, getAPIDataProvider, createClientStatusMessage]);

    const handleSubmitUnsubscription = useCallback(async () => {
        setIsLoadingSubmitUnsubscription(true);
        setIsErrorSubmitUnsubscription(false);

        const {unsubscriptionIDFull} = extractPathParts();

        if (!executeRecaptcha) {
            createClientStatusMessage({
                variant: 'danger',
                i18nKey: 'error.external-captcha-error',
                source: 'reCAPTCHA is not ready yet.'
            }, true);
            if (isMounted.current) {
                setIsErrorSubmitUnsubscription(true);
                setIsLoadingSubmitUnsubscription(false);
            }
            return;
        }

        let recaptchaToken;
        try {
            recaptchaToken = await executeRecaptcha();
        } catch (error) {
            createClientStatusMessage({
                variant: 'danger',
                i18nKey: 'error.external-captcha-error',
                source: `${error.message}`
            }, true);
            if (isMounted.current) {
                setIsErrorSubmitUnsubscription(true);
                setIsLoadingSubmitUnsubscription(false);
            }
            return;
        }

        try {
            await submitUnsubscription(unsubscriptionIDFull, recaptchaToken);
        } catch (error) {
            if (isMounted.current) {
                setIsErrorSubmitUnsubscription(true);
                setIsLoadingSubmitUnsubscription(false);
            }
            return;
        }

        if (isMounted.current) {
            setIsLoadedSubmitUnsubscription(true);
            setIsLoadingSubmitUnsubscription(false);
        }
    }, [extractPathParts, submitUnsubscription, createClientStatusMessage, executeRecaptcha]);

    useEffect(() => {
        if (!isLoadingAPIDataProvider && !isLoadedAPIDataProvider && !isErrorGetAPIDataProvider) {
            handleGetAPIDataProvider();
        }
    }, [isLoadedAPIDataProvider, isLoadingAPIDataProvider, isErrorGetAPIDataProvider, handleGetAPIDataProvider]);

    const loading = isLoadingAPIDataProvider || isLoadingSubmitUnsubscription;

    const renderCard = (content) => (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-12 col-md-9 col-lg-7 col-xl-6">
                    <div className="bg-white rounded shadow-sm p-4 p-md-5">
                        {content}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <Layout navSecondaryShow={true} className="bg-light" contentBackgroundArt={true}>
            {isLoadedAPIDataProvider && (
                <Helmet>
                    <title>{t('UnsubscribeFromProviderReports.page-head-title', {providerName: APIDataProvider.title})}</title>
                </Helmet>
            )}

            {!clientOnline && !isLoadedAPIDataProvider && renderCard(
                <div className="text-center text-muted">
                    <div>
                        <FontAwesomeIcon icon={faWifi}/>
                    </div>
                    <div className="mt-3">{t("Providers.message-offline")}</div>
                </div>
            )}

            {clientOnline && loading && renderCard(
                <div className="text-center">
                    <h2 className="h4 mb-3">Loading...</h2>
                    <p className="text-muted mb-0">Please wait while we prepare your unsubscribe request.</p>
                </div>
            )}

            {clientOnline && isNotValidUnsubscriptionID && renderCard(
                <>
                    <h2 className="h4 mb-3">Invalid unsubscribe link</h2>
                    <Alert variant="danger" className="mb-4">{t('UnsubscribeFromProviderReports.message-cto-unsubscribe-failure')}</Alert>
                    <div className="d-flex justify-content-end">
                        <Button onClick={() => history.replace('/')}>{t('UnsubscribeFromProviderReports.go-to-home')}</Button>
                    </div>
                </>
            )}

            {clientOnline && isNotFoundAPIDataProvider && renderCard(
                <>
                    <h2 className="h4 mb-3">Provider not found</h2>
                    <Alert variant="danger" className="mb-4">{t('UnsubscribeFromProviderReports.message-cto-unsubscribe-failure')}</Alert>
                    <div className="d-flex justify-content-end">
                        <Button onClick={() => history.replace('/')}>{t('UnsubscribeFromProviderReports.go-to-home')}</Button>
                    </div>
                </>
            )}

            {clientOnline && isErrorGetAPIDataProvider && renderCard(
                <>
                    <h2 className="h4 mb-3">Could not load unsubscribe page</h2>
                    <Alert variant="danger" className="mb-4">{t('UnsubscribeFromProviderReports.message-cto-unsubscribe-failure')}</Alert>
                    <div className="d-flex justify-content-between gap-2">
                        <Button variant="secondary" onClick={() => history.replace('/')}>{t('UnsubscribeFromProviderReports.go-to-home')}</Button>
                        <Button onClick={() => {
                            setIsErrorGetAPIDataProvider(false);
                            setIsLoadedAPIDataProvider(false);
                            setAPIDataProvider(null);
                        }}>Try again</Button>
                    </div>
                </>
            )}

            {clientOnline && isLoadedAPIDataProvider && !isErrorSubmitUnsubscription && !isLoadedSubmitUnsubscription && !loading && renderCard(
                <>
                    <h1 className="h3 mb-3">{t('UnsubscribeFromProviderReports.page-head-title', {providerName: APIDataProvider.title})}</h1>
                    <Alert variant="info" className="mb-4">{t('UnsubscribeFromProviderReports.message-cto-unsubscribe', {providerName: APIDataProvider.title})}</Alert>
                    <div className="d-flex justify-content-between gap-2 flex-wrap">
                        <Button
                            variant="secondary"
                            disabled={loading}
                            onClick={() => history.replace('/')}
                        >
                            {t('UnsubscribeFromProviderReports.cancel')}
                        </Button>
                        <Button
                            disabled={loading}
                            onClick={async (event) => {
                                event.preventDefault();
                                await handleSubmitUnsubscription();
                            }}
                        >
                            {t('UnsubscribeFromProviderReports.cto-unsubscribe-confirm')}
                        </Button>
                    </div>
                </>
            )}

            {clientOnline && isErrorSubmitUnsubscription && !loading && renderCard(
                <>
                    <h2 className="h4 mb-3">Unsubscribe failed</h2>
                    <Alert variant="danger" className="mb-4">{t('UnsubscribeFromProviderReports.message-cto-unsubscribe-failure')}</Alert>
                    <div className="d-flex justify-content-between gap-2 flex-wrap">
                        <Button variant="secondary" onClick={() => history.replace('/')}>{t('UnsubscribeFromProviderReports.cancel')}</Button>
                        <Button onClick={() => setIsErrorSubmitUnsubscription(false)}>{t('UnsubscribeFromProviderReports.try-again')}</Button>
                    </div>
                </>
            )}

            {clientOnline && isLoadedSubmitUnsubscription && !loading && renderCard(
                <>
                    <h2 className="h4 mb-3">Subscription updated</h2>
                    <Alert variant="success" className="mb-4">{t('UnsubscribeFromProviderReports.message-cto-unsubscribe-success')}</Alert>
                    <div className="d-flex justify-content-end">
                        <Button onClick={() => history.replace('/')}>{t('UnsubscribeFromProviderReports.go-to-home')}</Button>
                    </div>
                </>
            )}
        </Layout>
    )
}

export default UnsubscribeFromProviderReports;
